import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, Gift, User } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
interface OccasionModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDate: Date;
  onComplete: (occasion: string, customName?: string) => void;
}
const OCCASION_TYPES = [{
  id: 'birthday',
  label: 'Birthday',
  icon: '🎂'
}, {
  id: 'anniversary',
  label: 'Anniversary',
  icon: '💕'
}, {
  id: 'graduation',
  label: 'Graduation',
  icon: '🎓'
}, {
  id: 'wedding',
  label: 'Wedding',
  icon: '💍'
}, {
  id: 'baby-shower',
  label: 'Baby Shower',
  icon: '👶'
}, {
  id: 'engagement',
  label: 'Engagement',
  icon: '💐'
}, {
  id: 'valentines',
  label: "Valentine's Day",
  icon: '❤️'
}, {
  id: 'mothers-day',
  label: "Mother's Day",
  icon: '🌸'
}, {
  id: 'fathers-day',
  label: "Father's Day",
  icon: '👔'
}, {
  id: 'other',
  label: 'Other',
  icon: '✨'
}];
export function OccasionModal({
  isOpen,
  onClose,
  selectedDate,
  onComplete
}: OccasionModalProps) {
  const [step, setStep] = useState<'occasion' | 'custom'>('occasion');
  const [selectedOccasion, setSelectedOccasion] = useState<string>('');
  const [customOccasion, setCustomOccasion] = useState('');
  const handleOccasionSelect = (occasionId: string) => {
    setSelectedOccasion(occasionId);
    if (occasionId === 'other') {
      setStep('custom');
    } else {
      const occasion = OCCASION_TYPES.find(o => o.id === occasionId);
      onComplete(occasion?.label || occasionId);
    }
  };
  const handleCustomSubmit = () => {
    if (customOccasion.trim()) {
      onComplete(customOccasion.trim());
    }
  };
  const handleClose = () => {
    setStep('occasion');
    setSelectedOccasion('');
    setCustomOccasion('');
    onClose();
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-908">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onClick={handleClose} className="absolute inset-0 bg-black/40 backdrop-blur-sm" data-id="element-909" />

      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-id="element-910">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-rose-100 p-6 flex justify-between items-center z-10" data-id="element-911">
          <div data-id="element-912">
            <h2 className="text-2xl font-serif font-bold text-gray-900" data-id="element-913">
              {step === 'occasion' ? 'Choose Your Occasion' : 'Custom Occasion'}
            </h2>
            <p className="text-sm text-gray-500 mt-1 flex items-center gap-2" data-id="element-914">
              <Calendar className="w-4 h-4" data-id="element-915" />
              {selectedDate.toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
              year: 'numeric'
            })}
            </p>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-400 hover:text-gray-600" data-id="element-916">
            <X className="w-6 h-6" data-id="element-917" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6" data-id="element-918">
          <AnimatePresence mode="wait" data-id="element-919">
            {step === 'occasion' && <motion.div key="occasion-step" initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: 20
          }} className="space-y-4" data-id="element-920">
                <p className="text-gray-600 mb-6" data-id="element-921">
                  What are you celebrating? Select an occasion to continue with
                  your order.
                </p>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-id="element-922">
                  {OCCASION_TYPES.map(occasion => <motion.button key={occasion.id} whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} onClick={() => handleOccasionSelect(occasion.id)} className={`
                        p-4 rounded-xl border-2 transition-all text-left
                        ${selectedOccasion === occasion.id ? 'border-rose-500 bg-rose-50' : 'border-gray-200 hover:border-rose-300 hover:bg-rose-50/50'}
                      `} data-id="element-923">
                      <div className="text-3xl mb-2" data-id="element-924">{occasion.icon}</div>
                      <div className="font-medium text-gray-900" data-id="element-925">
                        {occasion.label}
                      </div>
                    </motion.button>)}
                </div>
              </motion.div>}

            {step === 'custom' && <motion.div key="custom-step" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-6" data-id="element-926">
                <div className="text-center mb-6" data-id="element-927">
                  <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-928">
                    <Gift className="w-8 h-8 text-rose-500" data-id="element-929" />
                  </div>
                  <p className="text-gray-600" data-id="element-930">
                    Tell us what you're celebrating! We'll create something
                    special for your unique occasion.
                  </p>
                </div>

                <Input label="Occasion Name" value={customOccasion} onChange={e => setCustomOccasion(e.target.value)} placeholder="e.g., Retirement Party, Housewarming, Promotion" required data-id="element-931" />

                <div className="flex gap-3" data-id="element-932">
                  <Button variant="outline" onClick={() => setStep('occasion')} className="flex-1" data-id="element-933">
                    Back
                  </Button>
                  <Button onClick={handleCustomSubmit} disabled={!customOccasion.trim()} className="flex-1" data-id="element-934">
                    Continue
                  </Button>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>;
}